<script setup>
// Sprint #47.37: modal "Enviar mensagem" 1-clique (Kinbox-style).
// Canal + telefone + msg livre (QR) ou template APPROVED (Cloud) -> Enviar.
// Orquestra endpoints NATIVOS (zero backend novo):
//   1. ContactsAPI.search (ultimos 8 digitos, filtro client-side com variantes
//      do nono digito - armadilha #36) -> reusa contato existente
//   2. ContactsAPI.create se nao existir
//   3. ConversationApi.create com message INLINE (1 request cria conv+msg,
//      mesmo caminho do compose nativo). template_params vai DENTRO de
//      message (top-level da msg, shape WhatsAppTemplateParser) -> o
//      SendOnWhatsappService envia o template; pra QR o outgoing dispara o
//      webhook Evolution normal.
// source_id = telefone sem + (explicito; convencao Evolution armadilha #28,
// igual ao wa_source_id nativo do Cloud). Dedup #47.36 protege o reply.
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import ContactsAPI from 'dashboard/api/contacts';
import ConversationApi from 'dashboard/api/conversations';

const props = defineProps({
  show: { type: Boolean, default: false },
});
const emit = defineEmits(['close']);

const store = useStore();
const route = useRoute();
const router = useRouter();

const inboxId = ref('');
const contactName = ref('');
const phone = ref('');
const body = ref('');
const templateName = ref('');
const templateVarValues = ref({});
const submitting = ref(false);
const errorMsg = ref(null);

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const currentUser = computed(() => store.getters.getCurrentUser);

const eligibleInboxes = computed(() =>
  inboxes.value.filter(
    i =>
      i.channel_type === 'Channel::Api' ||
      (i.channel_type === 'Channel::Whatsapp' &&
        i.provider === 'whatsapp_cloud')
  )
);

const selectedInbox = computed(() =>
  eligibleInboxes.value.find(i => i.id === Number(inboxId.value))
);
const isCloud = computed(
  () => selectedInbox.value?.channel_type === 'Channel::Whatsapp'
);

const approvedTemplates = computed(() => {
  if (!isCloud.value) return [];
  const templates =
    store.getters['inboxes/getWhatsAppTemplates'](inboxId.value) || [];
  return templates.filter(t => {
    if (t.status?.toUpperCase() !== 'APPROVED') return false;
    // v1: só templates de texto (header de mídia exige URL, fora do escopo)
    const mediaHeader = (t.components || []).some(
      c => c.type === 'HEADER' && c.format && c.format !== 'TEXT'
    );
    return !mediaHeader;
  });
});

const selectedTemplate = computed(() =>
  approvedTemplates.value.find(t => t.name === templateName.value)
);
const templateBody = computed(
  () =>
    (selectedTemplate.value?.components || []).find(c => c.type === 'BODY')
      ?.text || ''
);
// Variáveis {{1}}, {{2}}... do body — cada uma vira input obrigatório
const templateVars = computed(() => {
  const matches = templateBody.value.match(/{{\s*(\d+)\s*}}/g) || [];
  return [...new Set(matches.map(m => m.replace(/\D/g, '')))];
});
const vlabel = v => `{{${v}}}`;

// Preview do template com variáveis substituídas (vira o content da msg)
const renderedTemplate = computed(() =>
  templateBody.value.replace(/{{\s*(\d+)\s*}}/g, (match, v) =>
    templateVarValues.value[v]?.trim() ? templateVarValues.value[v] : match
  )
);

watch(templateName, () => {
  templateVarValues.value = {};
});
watch(inboxId, () => {
  templateName.value = '';
  errorMsg.value = null;
});

// ---- #47.37.2: combobox de template com busca (pattern #45.3.1/.2/.4) ----
// Teleport + 2 refs no click-outside (armadilha #21) + smart positioning
// (#45.3.4). Dropdown zIndex 10000 (modal e 9999).
const templateSearch = ref('');
const isComboOpen = ref(false);
const templateComboRef = ref(null);
const templateSearchInputRef = ref(null);
const comboDropdownRef = ref(null);
const comboPosition = ref({
  top: '0px',
  bottom: 'auto',
  left: '0px',
  width: '0px',
});
const DROPDOWN_ESTIMATE = 280;

const filteredTemplates = computed(() => {
  const q = templateSearch.value.toLowerCase().trim();
  if (!q) return approvedTemplates.value;
  return approvedTemplates.value.filter(t =>
    t.name.toLowerCase().includes(q)
  );
});

const templateButtonLabel = computed(() =>
  selectedTemplate.value
    ? `${selectedTemplate.value.name} · ${selectedTemplate.value.language}`
    : 'Selecione um template'
);

const updateComboPosition = () => {
  if (!templateComboRef.value) return;
  const r = templateComboRef.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - r.bottom;
  const spaceAbove = r.top;
  if (spaceBelow >= DROPDOWN_ESTIMATE || spaceBelow >= spaceAbove) {
    comboPosition.value = {
      top: `${r.bottom + 4}px`,
      bottom: 'auto',
      left: `${r.left}px`,
      width: `${r.width}px`,
    };
  } else {
    comboPosition.value = {
      top: 'auto',
      bottom: `${window.innerHeight - r.top + 4}px`,
      left: `${r.left}px`,
      width: `${r.width}px`,
    };
  }
};

const toggleCombo = () => {
  isComboOpen.value = !isComboOpen.value;
  if (isComboOpen.value) {
    updateComboPosition();
    nextTick(() => templateSearchInputRef.value?.focus());
  }
};
const closeCombo = () => {
  isComboOpen.value = false;
  templateSearch.value = '';
};
const selectTemplateOption = t => {
  templateName.value = t.name;
  closeCombo();
};
const onDocClick = e => {
  if (!isComboOpen.value) return;
  const inBtn = templateComboRef.value?.contains(e.target);
  const inDrop = comboDropdownRef.value?.contains(e.target);
  if (!inBtn && !inDrop) closeCombo();
};
const onWindowChange = () => {
  if (isComboOpen.value) updateComboPosition();
};
onMounted(() => {
  document.addEventListener('mousedown', onDocClick);
  window.addEventListener('resize', onWindowChange);
  window.addEventListener('scroll', onWindowChange, true);
});
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocClick);
  window.removeEventListener('resize', onWindowChange);
  window.removeEventListener('scroll', onWindowChange, true);
});

const phoneDigits = computed(() => phone.value.replace(/\D/g, ''));
const phoneValid = computed(() => /^\d{2}\d{8,9}$/.test(phoneDigits.value));
const fullDigits = computed(() => `55${phoneDigits.value}`);

const canSubmit = computed(() => {
  if (!inboxId.value || !phoneValid.value) return false;
  if (isCloud.value) {
    if (!templateName.value) return false;
    return templateVars.value.every(v => templateVarValues.value[v]?.trim());
  }
  return body.value.trim().length > 0;
});

const reset = () => {
  inboxId.value = '';
  contactName.value = '';
  phone.value = '';
  body.value = '';
  templateName.value = '';
  templateVarValues.value = {};
  errorMsg.value = null;
  isComboOpen.value = false;
  templateSearch.value = '';
};

const close = () => {
  if (submitting.value) return;
  reset();
  emit('close');
};

// Busca contato existente cobrindo variantes do nono dígito (armadilha #36):
// busca pelos últimos 8 dígitos (iguais nas 2 variantes) e valida DDD client-side.
const findExistingContact = async () => {
  const ddd = phoneDigits.value.slice(0, 2);
  const last8 = phoneDigits.value.slice(-8);
  try {
    const res = await ContactsAPI.search(last8, 1, 'name', '');
    const candidates = res?.data?.payload || [];
    return candidates.find(c => {
      const digits = (c.phone_number || '').replace(/\D/g, '');
      return digits.endsWith(last8) && digits.includes(`55${ddd}`);
    });
  } catch (e) {
    return null;
  }
};

const submit = async () => {
  if (!canSubmit.value || submitting.value) return;
  submitting.value = true;
  errorMsg.value = null;

  try {
    // 1. Contato: reusa existente ou cria
    let contact = await findExistingContact();
    if (!contact) {
      const createRes = await ContactsAPI.create({
        name: contactName.value.trim() || `+${fullDigits.value}`,
        phone_number: `+${fullDigits.value}`,
      });
      contact =
        createRes?.data?.payload?.contact || createRes?.data?.payload || createRes?.data;
    }
    if (!contact?.id) throw new Error('Não foi possível criar o contato');

    // 2. Conversa + primeira msg em 1 request (caminho do compose nativo)
    const message = isCloud.value
      ? {
          content: renderedTemplate.value,
          template_params: {
            name: selectedTemplate.value.name,
            category: selectedTemplate.value.category,
            language: selectedTemplate.value.language,
            namespace: selectedTemplate.value.namespace,
            processed_params: { ...templateVarValues.value },
          },
        }
      : { content: body.value.trim() };

    const convRes = await ConversationApi.create({
      inbox_id: Number(inboxId.value),
      contact_id: contact.id,
      source_id: fullDigits.value,
      assignee_id: currentUser.value?.id,
      message,
    });
    const conv = convRes?.data;
    const displayId = conv?.id || conv?.display_id;
    if (!displayId) throw new Error('Conversa criada mas sem id retornado');

    const accountId = route.params.accountId;
    reset();
    emit('close');
    router.push({
      name: 'inbox_conversation',
      params: { accountId, conversation_id: displayId },
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[BmtQuickSend] erro:', err);
    errorMsg.value =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      'Erro ao enviar';
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      @click.self="close"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-700"
        >
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">
            Enviar mensagem
          </h2>
          <button
            class="w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700"
            @click="close"
          >
            <span class="i-lucide-x size-5 text-slate-600 dark:text-slate-300" />
          </button>
        </div>

        <!-- Body -->
        <div class="p-5 space-y-4">
          <!-- Canal -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Canal
            </label>
            <!-- #47.37.1: vendor prefixes inline (armadilha #15 — appearance-none
                 Tailwind nao suprime seta no fork) + background-image:none mata a
                 setinha fantasma do SCSS + chevron Lucide a direita (padrao #45.3.4) -->
            <div class="relative">
              <select
                v-model="inboxId"
                class="w-full pl-3 pr-9 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                style="-webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: none;"
              >
                <option value="">Selecione o canal</option>
                <option v-for="inb in eligibleInboxes" :key="inb.id" :value="inb.id">
                  {{ inb.name }}
                </option>
              </select>
              <span
                class="i-lucide-chevron-down size-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400"
              />
            </div>
          </div>

          <!-- Contato (opcional) -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Contato
              <span class="text-slate-400 font-normal">(opcional)</span>
            </label>
            <input
              v-model="contactName"
              placeholder="Nome do contato (opcional)"
              class="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2.5 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <!-- Número -->
          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Número
              <span
                class="text-slate-400 cursor-help"
                title="DDD + número (ex: 11999999999)"
              >ⓘ</span>
            </label>
            <div class="flex gap-2">
              <div
                class="h-11 px-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 flex items-center gap-1.5 text-slate-700 dark:text-slate-300 font-medium shrink-0 cursor-default"
              >
                <span>🇧🇷 +55</span>
                <span class="i-lucide-chevron-down size-4 text-slate-400" />
              </div>
              <input
                v-model="phone"
                type="tel"
                placeholder="Número com DDD"
                class="reset-base h-11 flex-1 border border-slate-200 dark:border-slate-600 rounded-lg px-3 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <p
              v-if="phone && !phoneValid"
              class="text-xs text-red-600 mt-1"
            >
              Digite DDD + número (10 ou 11 dígitos)
            </p>
          </div>

          <!-- Template (Cloud) -->
          <div v-if="isCloud">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Modelo de mensagem
            </label>
            <div ref="templateComboRef" class="relative">
              <button
                type="button"
                class="w-full flex items-center justify-between gap-2 px-3 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-left focus:outline-none focus:ring-2 focus:ring-emerald-500"
                @click="toggleCombo"
              >
                <span
                  class="truncate"
                  :class="
                    selectedTemplate
                      ? 'text-slate-900 dark:text-white'
                      : 'text-slate-400'
                  "
                >
                  {{ templateButtonLabel }}
                </span>
                <span
                  class="i-lucide-chevron-down size-4 text-slate-400 shrink-0 transition-transform"
                  :class="isComboOpen ? 'rotate-180' : ''"
                />
              </button>
              <Teleport to="body">
                <div
                  v-if="isComboOpen"
                  ref="comboDropdownRef"
                  class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden"
                  :style="{
                    position: 'fixed',
                    top: comboPosition.top,
                    bottom: comboPosition.bottom,
                    left: comboPosition.left,
                    width: comboPosition.width,
                    zIndex: 10000,
                  }"
                >
                  <input
                    ref="templateSearchInputRef"
                    v-model="templateSearch"
                    type="text"
                    placeholder="Buscar template pelo nome..."
                    class="w-full text-sm border-b border-slate-200 dark:border-slate-700 px-3 py-2 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none"
                    @keydown.esc="closeCombo"
                  />
                  <div class="max-h-56 overflow-y-auto">
                    <button
                      v-for="t in filteredTemplates"
                      :key="`${t.name}-${t.language}`"
                      type="button"
                      class="w-full text-left px-3 py-1.5 text-sm border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-emerald-50 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100"
                      :class="
                        templateName === t.name
                          ? 'bg-emerald-50 dark:bg-slate-700 font-medium'
                          : ''
                      "
                      @click="selectTemplateOption(t)"
                    >
                      {{ t.name }}
                      <span class="text-xs text-slate-400">
                        ({{ t.language }} · {{ t.category }})
                      </span>
                    </button>
                    <div
                      v-if="approvedTemplates.length && !filteredTemplates.length"
                      class="px-3 py-3 text-xs text-amber-600"
                    >
                      Nenhum template casa com a busca
                    </div>
                    <div
                      v-if="!approvedTemplates.length"
                      class="px-3 py-3 text-xs text-slate-400"
                    >
                      Nenhum template aprovado neste canal
                    </div>
                  </div>
                </div>
              </Teleport>
            </div>

            <!-- Variáveis do template -->
            <div v-if="templateVars.length" class="mt-3 space-y-2">
              <div v-for="v in templateVars" :key="v">
                <label class="block text-xs text-slate-500 mb-0.5">
                  Variável {{ vlabel(v) }}
                </label>
                <input
                  v-model="templateVarValues[v]"
                  :placeholder="`Valor de ${vlabel(v)}`"
                  class="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <!-- Preview -->
            <div
              v-if="templateBody"
              class="mt-3 text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-900 rounded-lg p-3 whitespace-pre-wrap"
            >
              {{ renderedTemplate }}
            </div>
          </div>

          <!-- Mensagem livre (QR) -->
          <div v-else-if="inboxId">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              Mensagem
            </label>
            <textarea
              v-model="body"
              rows="4"
              placeholder="Escreva sua mensagem..."
              class="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2.5 resize-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <div
            v-if="errorMsg"
            class="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg p-3"
          >
            {{ errorMsg }}
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex justify-end gap-2 p-5 border-t border-slate-100 dark:border-slate-700"
        >
          <button
            class="px-4 py-2.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium"
            @click="close"
          >
            Cancelar
          </button>
          <button
            :disabled="!canSubmit || submitting"
            class="px-6 py-2.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:bg-slate-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
            @click="submit"
          >
            {{ submitting ? 'Enviando…' : 'Enviar' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
